/**
 * Skyyrizon Cinematic Product Card controller.
 * For any [data-sky-cc] card (rendered by skyyrizon-product-card-cinematic):
 *   - 3D tilt + parallax on the media on desktop (fine pointer only).
 *   - Hover (desktop) / first-tap (mobile) plays lazy spin/turntable video.
 *   - Quick add via /cart/add.js with cart-bubble sync + toast.
 *   - Offscreen + hidden-tab video pause. prefers-reduced-motion safe.
 * Analytics fire through the global bridge via data-sky-event attributes.
 */
(function () {
  'use strict';

  var TOAST_MS = 1800, DONE_MS = 1600;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  function loadVideo(video) {
    if (video.getAttribute('data-loaded') === 'true') return;
    video.querySelectorAll('source[data-src]').forEach(function (s) {
      s.src = s.getAttribute('data-src');
      s.removeAttribute('data-src');
    });
    video.load();
    video.setAttribute('data-loaded', 'true');
  }

  function playVideo(video) {
    loadVideo(video);
    video.muted = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
    video.classList.add('is-playing');
  }

  function pauseVideo(video, rewind) {
    video.pause();
    if (rewind) { try { video.currentTime = 0; } catch (e) {} }
    video.classList.remove('is-playing');
  }

  function firePreview(card) {
    if (window.SkyyrizonAnalytics) {
      window.SkyyrizonAnalytics.track('product_media_preview', card);
    }
  }

  function initTilt(card) {
    if (reduced || coarse) return;
    var media = card.querySelector('[data-sky-cc-media]');
    var inner = card.querySelector('[data-sky-cc-inner]');
    if (!media || !inner) return;
    var raf = null, rx = 0, ry = 0;
    function apply() { raf = null; inner.style.transform = 'rotateX(' + ry.toFixed(2) + 'deg) rotateY(' + rx.toFixed(2) + 'deg)'; }
    media.addEventListener('mousemove', function (e) {
      var r = media.getBoundingClientRect();
      rx = ((e.clientX - r.left) / r.width - 0.5) * 10;   // max ~5deg
      ry = -((e.clientY - r.top) / r.height - 0.5) * 10;
      if (raf === null) raf = window.requestAnimationFrame(apply);
    });
    media.addEventListener('mouseleave', function () {
      rx = 0; ry = 0;
      if (raf === null) raf = window.requestAnimationFrame(apply);
    });
  }

  function initMedia(card) {
    var video = card.querySelector('[data-sky-cc-video]');
    if (!video) return;
    var media = card.querySelector('[data-sky-cc-media]');
    var link = card.querySelector('.sky-cc__media-link');

    if (!coarse) {
      media.addEventListener('mouseenter', function () { if (!reduced) { playVideo(video); firePreview(card); } });
      media.addEventListener('mouseleave', function () { pauseVideo(video, true); });
    } else if (link) {
      link.addEventListener('click', function (e) {
        if (video.classList.contains('is-playing')) return; // 2nd tap follows link
        e.preventDefault();
        playVideo(video);
        firePreview(card);
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (!en.isIntersecting && !video.paused) pauseVideo(video, false); });
      }, { threshold: 0 }).observe(card);
    }
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) return;
    document.querySelectorAll('[data-sky-cc-video]').forEach(function (v) { if (!v.paused) pauseVideo(v, false); });
  });

  /* ── Quick add ─────────────────────────────────────────────────── */

  function toast(scope, msg) {
    var t = scope.querySelector('[data-sky-cc-toast]') || document.querySelector('[data-sky-cc-toast]');
    if (!t) return;
    var txt = t.querySelector('.sky-cc-toast__text');
    if (txt) txt.textContent = msg;
    t.classList.add('is-visible');
    window.clearTimeout(t._timer);
    t._timer = window.setTimeout(function () { t.classList.remove('is-visible'); }, TOAST_MS);
  }

  function syncCart(cart) {
    if (!cart || !cart.sections) return;
    ['cart-icon-bubble', 'cart-live-region-text'].forEach(function (key) {
      if (!cart.sections[key]) return;
      try {
        var doc = new DOMParser().parseFromString(cart.sections[key], 'text/html');
        var next = doc.querySelector('#shopify-section-' + key);
        var cur = document.getElementById('shopify-section-' + key);
        if (next && cur) cur.replaceWith(next);
      } catch (e) {}
    });
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'skyyrizon-cinematic-card', cartData: cart });
    }
  }

  function initForm(form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('.sky-cc__add');
      if (!btn || btn.disabled) return;
      btn.setAttribute('data-state', 'loading');
      var routes = (window.Shopify && window.Shopify.routes) || {};
      var url = (routes.cart_add_url || '/cart/add.js') + '?sections=cart-icon-bubble,cart-live-region-text';
      fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      })
        .then(function (r) { if (!r.ok) return r.json().then(function (x) { throw x; }); return r.json(); })
        .then(function (cart) {
          btn.setAttribute('data-state', 'done');
          toast(document, 'Added to cart');
          syncCart(cart);
          window.setTimeout(function () { btn.removeAttribute('data-state'); }, DONE_MS);
        })
        .catch(function (err) {
          btn.setAttribute('data-state', 'error');
          toast(document, (err && err.description) || 'Could not add');
          window.setTimeout(function () { btn.removeAttribute('data-state'); }, DONE_MS);
        });
    });
  }

  function init() {
    document.querySelectorAll('[data-sky-cc]').forEach(function (card) {
      initTilt(card);
      initMedia(card);
    });
    document.querySelectorAll('[data-sky-cc-add]').forEach(initForm);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
